/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { CustomFilterV2, EmptyState } from '../../../components/v2';
import {
  tableV2AreaSx,
  tableV2ContainerSx,
  tableV2MultilineGridSx,
} from '../../../components/v2/tableV2Styles';
import { canViewFinanceiroReport } from '../../../helpers/financeiroReportPermission';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import TableService from '../../../services/TableService';
import BaseService from '../../../services/BaseService';
import { downloadBlobResponse, parseBlobExportError } from '../../../utils/downloadBlobResponse';
import ChartsRow from './ChartsRow';
import ExportPdfDialog from './ExportPdfDialog';
import KpiStrip from './KpiStrip';
import SegmentDimensionTabs from './SegmentDimensionTabs';
import SegmentFilterChips from './SegmentFilterChips';
import SegmentViewDescription from './SegmentViewDescription';
import UsuarioRankingSection from './UsuarioRankingSection';
import UsuarioSummary from './UsuarioSummary';
import {
  DIMENSION_GERAL,
  DIMENSION_TO_SEGMENTOS_KEY,
  SEGMENT_ALL,
  getSegmentFilterLabel,
  isModularDimension,
  isSegmentFilterActive,
} from './segmentConstants';
import {
  EMPTY_REPORT,
  VALIDATE_SCOPE_OPTIONS,
  VALIDATE_SCOPE_VALIDADO,
  VALIDATE_SCOPE_VALIDADO_PENDENTE,
  buildDistribuicaoFromTransacoes,
  buildEvolucaoMensalFromTransacoes,
  buildKpisFromTransacoes,
  buildRankingFromTransacoes,
  buildSearchFromFilter,
  buildExportPdfPayload,
  categoriaFilterEqual,
  computeParetoInsight,
  filterTransacoesByCategoria,
  filterTransacoesByDimensionScope,
  filterTransacoesBySegment,
  filtersEqual,
  formatPeriodLabel,
  getDefaultFilter,
  getScopeLabel,
  hasFilterSearchParams,
  includesPendingValidation,
  isCompanyScope,
  isPeriodInvalid,
  normalizeBiResponse,
  normalizeFilterForApi,
  parseCategoriaFromSearch,
  parseFilterFromSearch,
  resolveRanking,
  resolveReportMetrics,
  resolveSegmentChipOptions,
  filterSegmentsWithCost,
} from './constants';
import {
  formatTransactionDate,
  renderCartaoCell,
  renderCategoriaCell,
  renderDescricaoCell,
  renderInspecaoCell,
  renderPagamentoCell,
  renderValidateCell,
  renderValorCell,
} from './tableCells';

const tipoPagamentoFilterOptions = [
  { id: 'APORTE', nome: 'Aporte' },
  { id: 'CARTAO_CORPORATIVO', nome: 'Cartão Corporativo' },
];

const RelatorioFinanceiroV2 = ({
  history,
  location,
  authData,
  moduleConfigurations,
}) => {
  const canView = canViewFinanceiroReport({ authData, moduleConfigurations });
  const [localFilter, setLocalFilter] = useState(() =>
    parseFilterFromSearch(location.search, getDefaultFilter()),
  );
  const [reportData, setReportData] = useState(EMPTY_REPORT);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [activeCategoria, setActiveCategoria] = useState(() =>
    parseCategoriaFromSearch(location.search),
  );
  const [openFilter, setOpenFilter] = useState(false);
  const [openExportPdf, setOpenExportPdf] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFeedback, setExportFeedback] = useState(null);
  const [reload, setReload] = useState(false);
  const [filterError, setFilterError] = useState(null);
  const [segmentOptionsCache, setSegmentOptionsCache] = useState({});
  const skipUrlSyncRef = useRef(false);
  const transacoesSectionRef = useRef(null);
  const companyContextRef = useRef({ dim: DIMENSION_GERAL, seg: SEGMENT_ALL });
  const localFilterRef = useRef(localFilter);
  const activeCategoriaRef = useRef(activeCategoria);
  localFilterRef.current = localFilter;
  activeCategoriaRef.current = activeCategoria;

  const syncUrlFromFilter = useCallback(
    (filter, categoria = activeCategoriaRef.current, { replace = true } = {}) => {
      const search = buildSearchFromFilter(filter, categoria);
      const pathname = location.pathname;
      const nextUrl = search ? `${pathname}?${search}` : pathname;
      const currentUrl = location.search ? `${pathname}${location.search}` : pathname;
      if (nextUrl === currentUrl) return;

      skipUrlSyncRef.current = true;
      if (replace) {
        history.replace(nextUrl);
      } else {
        history.push(nextUrl);
      }
    },
    [history, location.pathname, location.search],
  );

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }

    if (!hasFilterSearchParams(location.search)) {
      const defaults = getDefaultFilter();
      syncUrlFromFilter(defaults, null);
      if (!filtersEqual(defaults, localFilterRef.current)) {
        setLocalFilter(defaults);
        setActiveCategoria(null);
        setReload((prev) => !prev);
      }
      return;
    }

    const fromUrl = parseFilterFromSearch(location.search, getDefaultFilter());
    const fromCategoria = parseCategoriaFromSearch(location.search);

    if (!filtersEqual(fromUrl, localFilterRef.current)) {
      setLocalFilter(fromUrl);
      setActiveCategoria(null);
      setReload((prev) => !prev);
      return;
    }

    if (!categoriaFilterEqual(fromCategoria, activeCategoriaRef.current)) {
      setActiveCategoria(fromCategoria);
    }
  }, [location.search, syncUrlFromFilter]);

  const apiFilter = useMemo(
    () => normalizeFilterForApi(localFilter),
    [localFilter],
  );

  const periodInvalid = useMemo(
    () => isPeriodInvalid(localFilter.inicio, localFilter.fim),
    [localFilter.inicio, localFilter.fim],
  );

  const usuarioNomeFallback = useMemo(() => {
    if (!localFilter.usuarioId) return null;
    if (reportData.usuario?.nome) return reportData.usuario.nome;
    const fromRanking = (reportData.rankingUsuarios || []).find(
      (r) => r.usuarioId === localFilter.usuarioId,
    );
    return fromRanking?.nome || null;
  }, [localFilter.usuarioId, reportData.usuario, reportData.rankingUsuarios]);

  const isCompanyView = isCompanyScope(localFilter);
  const showUsuarioSummary = Boolean(localFilter.usuarioId);
  const showPendingAlert = includesPendingValidation(localFilter.validateScope);

  const contextLabel = useMemo(() => {
    const nome = reportData.usuario?.nome ?? usuarioNomeFallback;
    const parts = [getScopeLabel(nome)];
    const periodo = formatPeriodLabel(localFilter.inicio, localFilter.fim);
    if (periodo) parts.push(`Período: ${periodo}`);
    return parts.join(' · ');
  }, [reportData.usuario, usuarioNomeFallback, localFilter.inicio, localFilter.fim]);

  const fetchReport = useCallback(async () => {
    if (!canView) return;

    if (!apiFilter) {
      setReportData(EMPTY_REPORT);
      setFetchError(false);
      return;
    }

    setLoading(true);
    setFetchError(false);
    try {
      const response = await TableService.request('reports/financeiro/bi', apiFilter);
      const data = normalizeBiResponse(response?.data);
      setReportData(data);

      const dim = localFilterRef.current.dim || DIMENSION_GERAL;
      const seg = localFilterRef.current.seg || SEGMENT_ALL;
      if (
        !localFilterRef.current.usuarioId &&
        isModularDimension(dim) &&
        seg === SEGMENT_ALL
      ) {
        const segmentKey = DIMENSION_TO_SEGMENTOS_KEY[dim];
        const items = data.segmentos?.[segmentKey];
        if (segmentKey && items?.length) {
          setSegmentOptionsCache((prev) => ({ ...prev, [dim]: items }));
        }
      }
    } catch {
      setReportData(EMPTY_REPORT);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [apiFilter, reload, canView]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const activeDim = localFilter.dim || DIMENSION_GERAL;
  const activeSeg = localFilter.seg || SEGMENT_ALL;

  const dimensionScopedTransacoes = useMemo(
    () => filterTransacoesByDimensionScope(reportData.transacoes, activeDim),
    [reportData.transacoes, activeDim],
  );

  const segmentScopedTransacoes = useMemo(
    () => filterTransacoesBySegment(dimensionScopedTransacoes, activeDim, activeSeg),
    [dimensionScopedTransacoes, activeDim, activeSeg],
  );

  const scopedTransacoes = useMemo(
    () => filterTransacoesByCategoria(segmentScopedTransacoes, activeCategoria),
    [segmentScopedTransacoes, activeCategoria],
  );

  const derivedMetrics = useMemo(
    () => resolveReportMetrics(
      { ...reportData, transacoes: segmentScopedTransacoes },
      localFilter.inicio,
      localFilter.fim,
    ),
    [reportData, segmentScopedTransacoes, localFilter.inicio, localFilter.fim],
  );

  const baseDisplayData = useMemo(
    () => ({
      ...reportData,
      ...derivedMetrics,
      transacoes: segmentScopedTransacoes,
      rankingUsuarios: buildRankingFromTransacoes(segmentScopedTransacoes),
    }),
    [reportData, derivedMetrics, segmentScopedTransacoes],
  );

  const displayData = useMemo(() => {
    if (!activeCategoria) return baseDisplayData;
    return {
      ...baseDisplayData,
      kpis: buildKpisFromTransacoes(scopedTransacoes),
      evolucaoMensal: buildEvolucaoMensalFromTransacoes(
        scopedTransacoes,
        localFilter.inicio,
        localFilter.fim,
      ),
      distribuicao: buildDistribuicaoFromTransacoes(scopedTransacoes),
      transacoes: scopedTransacoes,
      rankingUsuarios: buildRankingFromTransacoes(scopedTransacoes),
    };
  }, [
    baseDisplayData,
    activeCategoria,
    scopedTransacoes,
    localFilter.inicio,
    localFilter.fim,
  ]);

  const onClearCategoria = useCallback(() => {
    setActiveCategoria(null);
    syncUrlFromFilter(localFilterRef.current, null);
  }, [syncUrlFromFilter]);

  const onSelectCategoria = useCallback(
    (categoria) => {
      const next = categoria || null;
      setActiveCategoria(next);
      syncUrlFromFilter(localFilterRef.current, next);
    },
    [syncUrlFromFilter],
  );

  const showSegmentChips = isCompanyView && isModularDimension(activeDim);
  const showSegmentComparison =
    isCompanyView
    && isModularDimension(activeDim)
    && activeSeg === SEGMENT_ALL
    && !activeCategoria;
  const showUsuarioRanking = isCompanyView;

  const companyRankingForSegments = useMemo(
    () => resolveRanking(displayData.rankingUsuarios, displayData.transacoes),
    [displayData.rankingUsuarios, displayData.transacoes],
  );

  const segmentChipOptions = useMemo(() => {
    if (!isModularDimension(activeDim)) return [];
    return resolveSegmentChipOptions({
      dimension: activeDim,
      apiSegmentos: reportData.segmentos,
      cacheOptions: segmentOptionsCache[activeDim],
      transacoes: reportData.transacoes,
    });
  }, [
    activeDim,
    segmentOptionsCache,
    reportData.segmentos,
    reportData.transacoes,
  ]);

  const showEmptySegmentAlert =
    showSegmentChips && !loading && segmentChipOptions.length === 0;

  const activeSegmentLabel = useMemo(() => {
    if (!isSegmentFilterActive(activeDim, activeSeg)) return null;
    const match = segmentChipOptions.find(
      (opt) => String(opt.key) === String(activeSeg),
    );
    return match?.label || activeSeg;
  }, [activeDim, activeSeg, segmentChipOptions]);

  const segmentFilterLabel = useMemo(
    () => getSegmentFilterLabel(activeDim, activeSeg, activeSegmentLabel),
    [activeDim, activeSeg, activeSegmentLabel],
  );

  const segmentItems = useMemo(() => {
    if (!showSegmentComparison) return [];
    return filterSegmentsWithCost(segmentChipOptions);
  }, [showSegmentComparison, segmentChipOptions]);

  const companyRanking = useMemo(
    () => (isCompanyView ? companyRankingForSegments : []),
    [isCompanyView, companyRankingForSegments],
  );

  const paretoInsight = useMemo(
    () => computeParetoInsight(companyRanking, displayData.kpis?.totalGasto ?? 0),
    [companyRanking, displayData.kpis?.totalGasto],
  );

  const onDimensionChange = useCallback(
    (dim) => {
      const nextFilter = {
        ...localFilterRef.current,
        dim,
        seg: SEGMENT_ALL,
      };
      setLocalFilter(nextFilter);
      setActiveCategoria(null);
      syncUrlFromFilter(nextFilter, null);
    },
    [syncUrlFromFilter],
  );

  const onSegmentChange = useCallback(
    (seg) => {
      const nextFilter = {
        ...localFilterRef.current,
        seg: seg || SEGMENT_ALL,
      };
      setLocalFilter(nextFilter);
      setActiveCategoria(null);
      syncUrlFromFilter(nextFilter, null);
      setReload((prev) => !prev);
    },
    [syncUrlFromFilter],
  );

  const onClearSegmentFilter = useCallback(() => {
    onSegmentChange(SEGMENT_ALL);
  }, [onSegmentChange]);

  const onClearUsuario = useCallback(() => {
    const nextFilter = {
      ...localFilterRef.current,
      usuarioId: null,
      dim: companyContextRef.current.dim,
      seg: companyContextRef.current.seg,
    };
    setLocalFilter(nextFilter);
    setActiveCategoria(null);
    syncUrlFromFilter(nextFilter, null);
    setReload((prev) => !prev);
  }, [syncUrlFromFilter]);

  const onSelectUsuario = useCallback(
    (usuarioId) => {
      if (!usuarioId) return;

      if (!localFilter.usuarioId) {
        companyContextRef.current = {
          dim: localFilter.dim || DIMENSION_GERAL,
          seg: localFilter.seg || SEGMENT_ALL,
        };
      }

      const nextFilter = {
        ...localFilter,
        usuarioId,
      };
      setLocalFilter(nextFilter);
      setActiveCategoria(null);
      syncUrlFromFilter(nextFilter, null);
      setReload((prev) => !prev);
      window.setTimeout(() => {
        transacoesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    },
    [localFilter, syncUrlFromFilter],
  );

  const tableRows = useMemo(
    () =>
      (displayData.transacoes || []).map((row, index) => ({
        id: row.id || `${row.usuarioId}-${row.data}-${row.tipoNota}-${index}`,
        ...row,
      })),
    [displayData.transacoes],
  );

  const openNotaInList = useCallback(
    (row) => {
      const params = new URLSearchParams();
      if (localFilter.inicio != null) {
        params.set('inicio', new Date(localFilter.inicio).toISOString().slice(0, 10));
      }
      if (localFilter.fim != null) {
        params.set('fim', new Date(localFilter.fim).toISOString().slice(0, 10));
      }
      if (row.usuarioId) params.set('inspetorId', row.usuarioId);
      if (row.tipoNota) params.set('tipoNota', row.tipoNota);
      const url = `/v2/notas?${params.toString()}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [localFilter.inicio, localFilter.fim],
  );

  const openCartao = useCallback((cartaoId) => {
    if (!cartaoId) return;
    const url = `/v2/cartoes?id=${encodeURIComponent(cartaoId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'data',
        headerName: 'Data',
        minWidth: 110,
        flex: 0.7,
        renderCell: (params) => formatTransactionDate(params.row.data),
      },
    ];

    if (isCompanyView) {
      cols.push({
        field: 'usuarioNome',
        headerName: 'Colaborador',
        minWidth: 140,
        flex: 1,
        renderCell: (params) => params.row.usuarioNome || '—',
      });
    }

    cols.push(
      {
        field: 'categoria',
        headerName: 'Categoria',
        minWidth: 150,
        flex: 0.9,
        renderCell: (params) =>
          renderCategoriaCell(params.row.categoria, params.row.tipoNota),
      },
      {
        field: 'descricao',
        headerName: 'Descrição',
        minWidth: 180,
        flex: 1.2,
        sortable: false,
        renderCell: (params) => renderDescricaoCell(params.row.descricao),
      },
      {
        field: 'tipoPagamento',
        headerName: 'Pagamento',
        minWidth: 130,
        flex: 0.8,
        renderCell: (params) => renderPagamentoCell(params.row.tipoPagamento),
      },
      {
        field: 'cartaoNumero',
        headerName: 'Cartão',
        minWidth: 100,
        renderCell: (params) =>
          renderCartaoCell(params.row.cartaoNumero, params.row.cartaoId, openCartao),
      },
      {
        field: 'inspecao',
        headerName: 'Inspeção',
        minWidth: 100,
        renderCell: (params) => renderInspecaoCell(params.row.inspecao),
      },
      {
        field: 'valor',
        headerName: 'Valor',
        minWidth: 120,
        flex: 0.7,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => renderValorCell(params.row.valor),
      },
      {
        field: 'actions',
        headerName: 'Ação',
        width: 72,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Tooltip title="Navegar">
            <IconButton
              size="small"
              onClick={() => openNotaInList(params.row)}
              aria-label="Navegar"
            >
              <OpenInNewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    );

    if (localFilter.validateScope === 'TODOS') {
      cols.splice(cols.length - 1, 0, {
        field: 'validate',
        headerName: 'Validação',
        minWidth: 140,
        renderCell: (params) => renderValidateCell(params.row.validate),
      });
    }

    return cols;
  }, [isCompanyView, localFilter.validateScope, openCartao, openNotaInList]);

  const onApplyFilter = (filterData) => {
    if (isPeriodInvalid(filterData.inicio, filterData.fim)) {
      setFilterError('A data de início deve ser anterior ou igual à data de fim.');
      return;
    }
    const usuarioId = filterData.usuarioId || filterData.inspetorId || null;
    if (usuarioId && !localFilter.usuarioId) {
      companyContextRef.current = {
        dim: localFilter.dim || DIMENSION_GERAL,
        seg: localFilter.seg || SEGMENT_ALL,
      };
    }
    const nextFilter = {
      ...filterData,
      usuarioId,
      inspetorId: undefined,
      dim: usuarioId ? DIMENSION_GERAL : (localFilter.dim || DIMENSION_GERAL),
      seg: usuarioId ? SEGMENT_ALL : (localFilter.seg || SEGMENT_ALL),
      validateScope: filterData.validateScope || VALIDATE_SCOPE_VALIDADO,
    };
    setFilterError(null);
    setLocalFilter(nextFilter);
    setActiveCategoria(null);
    syncUrlFromFilter(nextFilter, null);
    setOpenFilter(false);
    setReload((prev) => !prev);
  };

  const onExportPdf = async ({ secoes, applyFilters }) => {
    const payload = buildExportPdfPayload({
      filter: localFilter,
      secoes,
      activeCategoria,
      applyFilters,
    });

    if (!payload) {
      setExportFeedback({
        severity: 'error',
        message: 'Selecione ao menos uma seção para exportação.',
      });
      return;
    }

    setExportLoading(true);
    try {
      const response = await BaseService.execute({
        url: 'reports/financeiro/pdf',
        method: 'POST',
        data: payload,
        options: { responseType: 'blob' },
      });
      await downloadBlobResponse(response, 'relatorio-financeiro.pdf');
      setOpenExportPdf(false);
      setExportFeedback({
        severity: 'success',
        message: 'PDF exportado com sucesso!',
      });
    } catch (error) {
      const message = await parseBlobExportError(
        error,
        'Não foi possível exportar o PDF.',
      );
      setExportFeedback({
        severity: 'error',
        message,
      });
    } finally {
      setExportLoading(false);
    }
  };

  if (!canView) {
    return (
      <Box>
        <EmptyState
          title="Acesso restrito"
          message="O Relatório Financeiro não está disponível para o seu usuário. Solicite acesso ao administrador."
        />
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Box>
            <Typography variant="h4">Relatório Financeiro</Typography>
            {contextLabel && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 0.25, flexWrap: 'wrap', gap: 0.5 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {contextLabel}
                </Typography>
                {showUsuarioSummary && (reportData.usuario?.nome || usuarioNomeFallback) && (
                  <Chip
                    size="small"
                    label={reportData.usuario?.nome || usuarioNomeFallback}
                    onDelete={onClearUsuario}
                  />
                )}
                {activeCategoria && (
                  <Chip
                    size="small"
                    label={`Categoria: ${activeCategoria}`}
                    onDelete={onClearCategoria}
                  />
                )}
              </Stack>
            )}
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {activeCategoria && (
              <Button
                variant="outlined"
                startIcon={<ArrowBackOutlinedIcon />}
                onClick={onClearCategoria}
              >
                Ver todas as categorias
              </Button>
            )}
            {showUsuarioSummary && (
              <Button
                variant="outlined"
                startIcon={<ArrowBackOutlinedIcon />}
                onClick={onClearUsuario}
              >
                Ver todos
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<FilterListOutlinedIcon />}
              onClick={() => setOpenFilter(true)}
            >
              Filtros
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfOutlinedIcon />}
              disabled={loading}
              onClick={() => setOpenExportPdf(true)}
            >
              Exportar PDF
            </Button>
          </Stack>
        </Stack>

        {showPendingAlert && (
          <Alert
            severity="warning"
            variant="outlined"
            icon={<WarningAmberOutlinedIcon />}
            sx={{ borderRadius: 2 }}
          >
            {localFilter.validateScope === VALIDATE_SCOPE_VALIDADO_PENDENTE
              ? 'Inclui notas aguardando validação — valores podem mudar após aprovação.'
              : 'Inclui notas invalidadas e pendentes — use com cautela para análise administrativa.'}
          </Alert>
        )}

        {fetchError && (
          <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
            Não foi possível carregar os dados do relatório. Verifique o período
            {isCompanyView ? '' : ' e o colaborador'} e tente novamente.
          </Alert>
        )}

        <UsuarioSummary
          usuario={reportData.usuario}
          kpis={displayData.kpis}
          loading={loading}
          visible={showUsuarioSummary}
          onClose={onClearUsuario}
        />

        {isCompanyView && (
          <Box sx={{ pr: 2 }}>
            <SegmentDimensionTabs
              value={activeDim}
              onChange={onDimensionChange}
              disabled={loading}
            />
            {showSegmentChips && (
              <Box sx={{ mt: 1.5, mb: 1.5 }}>
                <SegmentFilterChips
                  options={segmentChipOptions}
                  value={activeSeg}
                  loading={loading && segmentChipOptions.length === 0}
                  onChange={onSegmentChange}
                />
                {showEmptySegmentAlert && (
                  <Alert severity="info" variant="outlined" sx={{ mt: 1.5, borderRadius: 2 }}>
                    Nenhum segmento com gasto no período selecionado.
                  </Alert>
                )}
              </Box>
            )}
            <SegmentViewDescription
              dim={activeDim}
              seg={activeSeg}
              segmentLabel={activeSegmentLabel}
              loading={loading}
            />
          </Box>
        )}

        <KpiStrip
          kpis={displayData.kpis}
          categoryKpis={activeCategoria ? baseDisplayData.kpis : displayData.kpis}
          loading={loading}
          activeCategoria={activeCategoria}
          onSelectCategoria={onSelectCategoria}
        />

        <ChartsRow
          evolucaoMensal={displayData.evolucaoMensal}
          distribuicao={displayData.distribuicao}
          totalGasto={displayData.kpis?.totalGasto ?? 0}
          loading={loading}
        />

        {isCompanyView && (
          <UsuarioRankingSection
            showSegmentComparison={showSegmentComparison}
            showUsuarioRanking={showUsuarioRanking}
            ranking={companyRanking}
            segmentItems={segmentItems}
            paretoInsight={paretoInsight}
            totalGasto={displayData.kpis?.totalGasto ?? 0}
            loading={loading}
            onSelectUsuario={onSelectUsuario}
            onSelectSegment={onSegmentChange}
          />
        )}

        <Box ref={transacoesSectionRef}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, pr: 2 }}>
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Typography variant="h6">Notas</Typography>
            {segmentFilterLabel && (
              <Chip
                size="small"
                label={segmentFilterLabel}
                onDelete={onClearSegmentFilter}
                sx={{ ml: 0.5 }}
              />
            )}
            {activeCategoria && (
              <Chip
                size="small"
                label={activeCategoria}
                onDelete={onClearCategoria}
                sx={{ ml: 0.5 }}
              />
            )}
          </Stack>

          {!loading && tableRows.length === 0 ? (
            <EmptyState
              title="Nenhuma nota encontrada"
              message={
                activeCategoria
                  ? `Não há notas na categoria "${activeCategoria}" no período selecionado.`
                  : segmentFilterLabel
                    ? `Não há gastos para o recorte "${segmentFilterLabel}" no período selecionado.`
                    : isCompanyView
                      ? 'Não há notas registradas no período selecionado.'
                      : 'Não há notas para este colaborador no período selecionado.'
              }
              onClearFilters={
                activeCategoria
                  ? onClearCategoria
                  : segmentFilterLabel
                    ? onClearSegmentFilter
                    : undefined
              }
            />
          ) : (
            <Box sx={tableV2ContainerSx}>
              <Box sx={{ ...tableV2AreaSx, minHeight: 420 }}>
                <DataGrid
                  rows={tableRows}
                  columns={columns}
                  loading={loading}
                  disableRowSelectionOnClick
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25, page: 0 } },
                  }}
                  rowHeight={52}
                  getRowHeight={() => 'auto'}
                  localeText={{
                    ...ptBR.components.MuiDataGrid.defaultProps.localeText,
                    noRowsLabel: 'Nenhuma nota encontrada',
                  }}
                  sx={{
                    ...tableV2MultilineGridSx,
                    border: 0,
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

        {filterError && (
          <Snackbar
            open
            autoHideDuration={6000}
            onClose={() => setFilterError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="warning" onClose={() => setFilterError(null)} sx={{ width: '100%' }}>
              {filterError}
            </Alert>
          </Snackbar>
        )}

        {exportFeedback && (
          <Snackbar
            open
            autoHideDuration={6000}
            onClose={() => setExportFeedback(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              severity={exportFeedback.severity}
              onClose={() => setExportFeedback(null)}
              sx={{ width: '100%' }}
            >
              {exportFeedback.message}
            </Alert>
          </Snackbar>
        )}

        <ExportPdfDialog
          open={openExportPdf}
          onClose={() => {
            if (!exportLoading) setOpenExportPdf(false);
          }}
          onExport={onExportPdf}
          loading={exportLoading}
          filter={localFilter}
          activeCategoria={activeCategoria}
          usuarioNome={reportData.usuario?.nome || usuarioNomeFallback}
          segmentLabel={activeSegmentLabel}
          periodInvalid={periodInvalid}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => {
            setOpenFilter(false);
            setFilterError(null);
          }}
          title="Filtros"
          fullWidth
          defaultFilter={getDefaultFilter()}
          appliedFilter={{
            ...localFilter,
            inspetorId: localFilter.usuarioId,
          }}
          confirmButtonText="Aplicar"
          fields={[
            { field: 'useDates', md: 3 },
            {
              field: 'useInspetor',
              multiSelect: false,
              md: 12,
            },
            {
              field: 'useSelectOption',
              key: 'validateScope',
              label: 'Validação',
              options: VALIDATE_SCOPE_OPTIONS,
              md: 6,
            },
            {
              field: 'useSelectOption',
              key: 'tipoPagamento',
              label: 'Tipo Pagamento',
              options: tipoPagamentoFilterOptions,
              md: 6,
            },
            { field: 'useCartao', md: 6 },
            {
              field: 'useSelectOption',
              key: 'inspecao',
              label: 'Inspeção',
              options: [
                { id: 'true', nome: 'Sim' },
                { id: 'false', nome: 'Não' },
              ],
              md: 6,
            },
          ]}
          onFilter={onApplyFilter}
        />
      </Stack>
    </Box>
  );
};

RelatorioFinanceiroV2.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func, replace: PropTypes.func }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  authData: PropTypes.object.isRequired,
  moduleConfigurations: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(RelatorioFinanceiroV2),
);
